import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
  Grid,
  Typography,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Form from 'react-bootstrap/Form';

import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import NoDataImg from '../../../assets/img/no-data.gif';
import {
  updateTopHeader,
  updateLoading,
} from '../../../redux/features/common/commonSlice';
import urls from '../../constants/apiurl';
import ApiCaller from '../../constants/ApiCaller';
import { showDangerToast, showToast } from '../../constants/toaster';
import InsufficientPermission from '../../custom/InSufficientPermission';

export const DiscountVoucherConfig = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.userData);
  const userPermissions = useSelector((state) => state?.permission);

  // State management
  const [countries, setCountries] = useState([]);
  const [unitConfigs, setUnitConfigs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    countryId: '',
    countryName: '',
    currencyCode: '',
    currencySymbol: '',
    purchaseValue: '',
    redeemValue: '',
    countryCode: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Country search dropdown state
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // Modal country search dropdown state
  const [showModalCountryDropdown, setShowModalCountryDropdown] = useState(false);
  const [modalCountrySearchTerm, setModalCountrySearchTerm] = useState('');
  const [modalDropdownPosition, setModalDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Filtered countries based on search term
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
    country.currencyCode.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  // Filtered countries for modal
  const modalFilteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(modalCountrySearchTerm.toLowerCase()) ||
    country.currencyCode.toLowerCase().includes(modalCountrySearchTerm.toLowerCase())
  );

  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    pageNo: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  });
  const [selectedCountryFilter, setSelectedCountryFilter] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [order, setOrder] = useState(true); // true for ascending, false for descending

  // Permission checks
  let currentModulePermission;
  let addPermission = true;
  let editPermission = true;

  if (userPermissions?.length > 0) {
    currentModulePermission = userPermissions?.find(
      (permission) => permission?.name === 'DISCOUNT_VOUCHERS'
    );
    if (currentModulePermission) {
      addPermission = currentModulePermission?.add;
      editPermission = currentModulePermission?.edit;
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader('Configure Discount Voucher Units'));
    fetchCountries();
    fetchUnitConfigs();
  }, [dispatch]);

  // Refetch configs when pagination or filter changes
  useEffect(() => {
    fetchUnitConfigs();
  }, [pagination.pageNo, selectedCountryFilter, sortBy, order]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowCountryDropdown(false);
      }
      if (!event.target.closest('.modal-dropdown')) {
        setShowModalCountryDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Fetch countries from API
  const fetchCountries = async () => {
    try {
      setLoading(true);
      dispatch(updateLoading(true));

      let path = urls.countryCodeList;
      let res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status == 200 || response.status === "200") {
        setCountries(response?.data || []);
      } else {
        showDangerToast(response?.message || 'Failed to fetch countries');
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      showDangerToast('Failed to fetch countries');
    } finally {
      setLoading(false);
      dispatch(updateLoading(false));
    }
  };

  // Fetch existing unit configurations
  const fetchUnitConfigs = async () => {
    try {
      setLoading(true);
      dispatch(updateLoading(true));

      // Build query parameters
      let path = urls.discountVoucherUnitConfigList +
        "?pageNo=" + pagination.pageNo +
        "&pageSize=" + pagination.pageSize +
        "&sortBy=" + sortBy +
        "&order=" + order +
        "&countryId=" + (selectedCountryFilter?.id || '');

      let res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status == 200 || response.status === "200") {
        const data = response?.data;
        setUnitConfigs(data?.content || data?.items || []);
        setPagination(prev => ({
          ...prev,
          totalPages: data?.totalPages || 0,
          totalElements: data?.totalElements || data?.totalItems || 0,
        }));
      } else {
        showDangerToast(response?.message || 'Failed to fetch unit configurations');
      }
    } catch (error) {
      console.error('Error fetching unit configs:', error);
      showDangerToast('Failed to fetch unit configurations');
    } finally {
      setLoading(false);
      dispatch(updateLoading(false));
    }
  };

  // Handle pagination change
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      pageNo: newPage - 1, // MUI Pagination is 1-based, API is 0-based
    }));
  };

  // Clear country filter
  const clearCountryFilter = () => {
    setSelectedCountryFilter(null);
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setPagination(prev => ({
      ...prev,
      pageNo: 0,
    }));
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle country selection
  const handleCountrySelect = (countryId) => {
    const selectedCountry = countries.find(country => country.id === countryId);
    if (selectedCountry) {
      setFormData(prev => ({
        ...prev,
        countryId: selectedCountry.id,
        countryName: selectedCountry.name,
        currencyCode: selectedCountry.currencyCode,
        currencySymbol: selectedCountry.currencySymbol,
        countryCode: selectedCountry.isnCode,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.countryId) {
      newErrors.countryId = 'Please select a country';
    }

    if (!formData.purchaseValue || parseFloat(formData.purchaseValue) <= 0) {
      newErrors.purchaseValue = 'Purchase value must be greater than 0';
    }

    if (!formData.redeemValue || parseFloat(formData.redeemValue) <= 0) {
      newErrors.redeemValue = 'Redeem value must be greater than 0';
    }

    // Check if country already exists (only for new entries)
    if (!editingConfig && unitConfigs.some(config => config.countryId === formData.countryId)) {
      newErrors.countryId = 'Configuration for this country already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Open dialog for add/edit
  const openAddEditDialog = (config = null) => {
    if (config) {
      // Edit mode
      setEditingConfig(config);
      setFormData({
        countryId: config.countryId,
        countryName: config.countryName,
        currencyCode: config.currencyCode,
        currencySymbol: config.currencySymbol,
        purchaseValue: config.purchaseValue.toString(),
        redeemValue: config.redeemValue.toString(),
        countryCode: config.countryCode,
      });
    } else {
      // Add mode
      setEditingConfig(null);
      setFormData({
        countryId: '',
        countryName: '',
        currencyCode: '',
        currencySymbol: '',
        purchaseValue: '',
        redeemValue: '',
        countryCode: '',
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  // Close dialog
  const closeDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setFormData({
      countryId: '',
      countryName: '',
      currencyCode: '',
      currencySymbol: '',
      purchaseValue: '',
      redeemValue: '',
    });
    setErrors({});
    setShowModalCountryDropdown(false);
    setModalCountrySearchTerm('');
  };

  // Save configuration
  const saveConfig = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      dispatch(updateLoading(true));

      const configData = {
        countryId: formData.countryId,
        countryName: formData.countryName,
        currencyCode: formData.currencyCode,
        currencySymbol: formData.currencySymbol,
        countryCode: formData.countryCode,
        purchaseValue: parseFloat(formData.purchaseValue),
        redeemValue: parseFloat(formData.redeemValue),
      };

      // Use same add API for both add and edit operations
      // Backend will handle update based on countryId
      let path = urls.discountVoucherUnitConfig;
      let res = await ApiCaller.post(configData, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status == 200 || response.status === "200") {
        if (editingConfig) {
          showToast('Unit configuration updated successfully');
        } else {
          showToast('Unit configuration added successfully');
        }
        fetchUnitConfigs(); // Refresh the list
      } else {
        showDangerToast(response?.message || `Failed to ${editingConfig ? 'update' : 'add'} configuration`);
      }

      closeDialog();
    } catch (error) {
      console.error('Error saving config:', error);
      showDangerToast(`Failed to ${editingConfig ? 'update' : 'save'} configuration`);
    } finally {
      setLoading(false);
      dispatch(updateLoading(false));
    }
  };

  // Toggle configuration status
  const toggleConfigStatus = async (configId, currentStatus) => {
    try {
      setLoading(true);
      dispatch(updateLoading(true));

      let path = urls.discountVoucherUnitConfigStatusChange + '/' + configId;
      let res = await ApiCaller.put({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status == 200 || response.status === "200") {
        const statusText = currentStatus ? 'deactivated' : 'activated';
        showToast(`Configuration ${statusText} successfully`);
        fetchUnitConfigs(); // Refresh the list
      } else {
        showDangerToast(response?.message || 'Failed to change configuration status');
      }
    } catch (error) {
      console.error('Error changing config status:', error);
      showDangerToast('Failed to change configuration status');
    } finally {
      setLoading(false);
      dispatch(updateLoading(false));
    }
  };

  // Permission check
  if (!addPermission && !editPermission) {
    return <InsufficientPermission />;
  }

  return (
    <>
      <NavBar />
      <div className="main-content">
        <NavBarTop />
        <div className="header">
          <div className="logo">
            <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
          </div>
        </div>
        <div className="container-fluid mt--7">
          <div className="row">
            <div className="col text-left">
              <div className="card shadow">
                <div className="card-header border-0">
                  <div className="row align-items-center text-left">
                    <div className="col">
                      <h3 className="mb-0">Discount Voucher Unit Configuration</h3>
                    </div>
                    <div className="col text-right">
                      {addPermission && (
                        <a
                          onClick={() => openAddEditDialog()}
                          className="btn btn-sm btn-primary text-white"
                        >
                          <AddIcon />
                          Add Configuration
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-header border-0">
                  <div className="row align-items-center text-left">
                    <div className="col-md-6 mb-3">
                      <div className="dropdown">
                        <input
                          type="text"
                          className="form-control dropdown-toggle"
                          placeholder="Filter by Country"
                          value={selectedCountryFilter ? `${selectedCountryFilter.name} (${selectedCountryFilter.currencyCode})` : ''}
                          onChange={(e) => {
                            const searchValue = e.target.value.toLowerCase();
                            if (!searchValue) {
                              setSelectedCountryFilter(null);
                              setPagination(prev => ({
                                ...prev,
                                pageNo: 0,
                              }));
                            }
                          }}
                          onFocus={() => setShowCountryDropdown(true)}
                          readOnly={!showCountryDropdown}
                          style={{
                            borderRadius: "10px",
                            cursor: "pointer",
                            backgroundColor: "white"
                          }}
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded={showCountryDropdown}
                        />
                        {showCountryDropdown && (
                          <div
                            className="dropdown-menu show"
                            style={{
                              width: "100%",
                              maxHeight: "200px",
                              overflowY: "auto",
                              borderRadius: "10px",
                              border: "1px solid #dee2e6"
                            }}
                          >
                            <div className="px-3 py-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search countries..."
                                value={countrySearchTerm}
                                onChange={(e) => setCountrySearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: "14px" }}
                              />
                            </div>
                            {filteredCountries.length > 0 ? (
                              filteredCountries.map((country) => (
                                <button
                                  key={country.id}
                                  className="dropdown-item"
                                  onClick={() => {
                                    setSelectedCountryFilter(country);
                                    setShowCountryDropdown(false);
                                    setCountrySearchTerm('');
                                    setPagination(prev => ({
                                      ...prev,
                                      pageNo: 0,
                                    }));
                                  }}
                                  style={{
                                    textAlign: "left",
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                    transition: "background-color 0.15s ease-in-out"
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                >
                                  <div>
                                    <div style={{ fontWeight: "500" }}>{country.name}</div>
                                    <small className="text-muted">
                                      {country.currencySymbol} {country.currencyCode}
                                    </small>
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="dropdown-item text-muted">
                                No countries found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      {selectedCountryFilter && (
                        <Button
                          variant="contained"
                          className="custom-filter-btn"
                          onClick={clearCountryFilter}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  {unitConfigs.length > 0 ? (
                    <>
                      <table className="table align-items-center table-flush">
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">Country</th>
                            <th scope="col">Currency</th>
                            <th scope="col">Purchase Value (1 Unit)</th>
                            <th scope="col">Redeem Value (1 Unit)</th>
                            <th scope="col">Status</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unitConfigs.map((config) => (
                            <tr key={config.id}>
                              <td>
                                <div className="media align-items-center">
                                  <div className="media-body">
                                    <span className="mb-0 text-sm">
                                      {config.countryName}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Chip
                                  label={`${config.currencySymbol} ${config.currencyCode}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </td>
                              <td>
                                <span className="mb-0 text-sm">
                                  {config.currencySymbol} {config.purchaseValue?.toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <span className="mb-0 text-sm">
                                  {config.currencySymbol} {config.redeemValue?.toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={config.status || false}
                                      onChange={() => toggleConfigStatus(config.id, config.status)}
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Chip
                                      label={config.status ? 'Active' : 'Inactive'}
                                      color={config.status ? 'success' : 'default'}
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  }
                                  labelPlacement="end"
                                />
                              </td>
                              <td className="text-right">
                                {editPermission && (
                                  <IconButton
                                    onClick={() => openAddEditDialog(config)}
                                    color="primary"
                                    size="small"
                                    title="Edit"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-4">
                          <span className="mr-3">
                            Showing {pagination.pageNo * pagination.pageSize + 1} - {Math.min((pagination.pageNo + 1) * pagination.pageSize, pagination.totalElements)} of {pagination.totalElements} results
                          </span>
                          <Pagination
                            count={pagination.totalPages}
                            page={pagination.pageNo + 1} // MUI is 1-based
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <img src={NoDataImg} alt="No data" style={{ width: 200, height: 200 }} />
                      <h6 className="mt-3">No unit configurations found</h6>
                      <p className="text-muted">Add your first unit configuration to get started</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {editingConfig ? 'Edit Unit Configuration' : 'Add Unit Configuration'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                💡 Select a country and set the purchase and redeem values for 1 unit in that country's currency.
              </Typography>
            </Grid>

            {!editingConfig && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
                    Select Country <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <div className="modal-dropdown" style={{ position: 'relative', zIndex: 1 }}>
                    <input
                      type="text"
                      className={`form-control ${errors.countryId ? 'is-invalid' : ''}`}
                      placeholder="Choose a country..."
                      value={formData.countryId ? `${formData.countryName} (${formData.currencyCode})` : ''}
                      onFocus={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        setModalDropdownPosition({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                        setShowModalCountryDropdown(true);
                      }}
                      readOnly
                      style={{
                        cursor: "pointer",
                        backgroundColor: "white",
                        border: errors.countryId ? '1px solid #dc3545' : '1px solid #ced4da',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        transition: 'all 0.2s ease'
                      }}
                    />
                    {showModalCountryDropdown && createPortal(
                      <div
                        className="dropdown-menu show"
                        style={{
                          width: modalDropdownPosition.width + "px",
                          maxHeight: "280px",
                          overflowY: "auto",
                          position: "absolute",
                          top: modalDropdownPosition.top + "px",
                          left: modalDropdownPosition.left + "px",
                          zIndex: 99999,
                          border: "1px solid #ced4da",
                          borderRadius: "12px",
                          backgroundColor: "white",
                          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                        }}
                      >
                        <div className="px-3 py-3 border-bottom" style={{ borderBottom: '1px solid #e9ecef' }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="🔍 Search countries..."
                            value={modalCountrySearchTerm}
                            onChange={(e) => setModalCountrySearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: "14px",
                              borderRadius: "8px",
                              border: "1px solid #e3e6f0",
                              padding: "8px 12px",
                              transition: "border-color 0.15s ease-in-out"
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
                          {modalFilteredCountries.length > 0 ? (
                            modalFilteredCountries.map((country) => (
                              <button
                                key={country.id}
                                className="dropdown-item"
                                onClick={() => {
                                  handleCountrySelect(country.id);
                                  setShowModalCountryDropdown(false);
                                  setModalCountrySearchTerm('');
                                }}
                                style={{
                                  textAlign: "left",
                                  padding: "12px 16px",
                                  border: "none",
                                  width: "100%",
                                  backgroundColor: "transparent",
                                  transition: "background-color 0.15s ease-in-out",
                                  borderRadius: "8px",
                                  margin: "2px 0"
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fc"}
                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                              >
                                <div>
                                  <div style={{ fontWeight: "600", fontSize: "14px", color: "#2c2c54", marginBottom: "2px" }}>
                                    {country.name}
                                  </div>
                                  <small style={{ color: "#6c757d", fontSize: "12px" }}>
                                    {country.currencySymbol} {country.currencyCode}
                                  </small>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-4 text-center" style={{ color: "#6c757d" }}>
                              <div style={{ fontSize: "14px" }}>No countries found</div>
                              <small>Try adjusting your search terms</small>
                            </div>
                          )}
                        </div>
                      </div>,
                      document.body
                    )}
                    {errors.countryId && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {errors.countryId}
                      </div>
                    )}
                  </div>
                </Box>
              </Grid>
            )}

            {formData.countryId && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ mt: 1, mb: 2, p: 2, backgroundColor: '#f8f9fc', borderRadius: 2, border: '1px solid #e3e6f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#5a5c69' }}>
                      🏛️ Selected Country Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Country"
                          value={formData.countryName}
                          disabled
                          size="small"
                          sx={{
                            '& .MuiInputBase-input': {
                              backgroundColor: '#fff',
                              fontWeight: 500
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Currency"
                          value={`${formData.currencySymbol} ${formData.currencyCode}`}
                          disabled
                          size="small"
                          sx={{
                            '& .MuiInputBase-input': {
                              backgroundColor: '#fff',
                              fontWeight: 500
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                    💰 Unit Values Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`Purchase Value (${formData.currencySymbol})`}
                    value={formData.purchaseValue}
                    onChange={(e) => handleInputChange('purchaseValue', e.target.value)}
                    error={!!errors.purchaseValue}
                    helperText={errors.purchaseValue || 'Amount user pays for 1 unit'}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#5e72e4'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`Redeem Value (${formData.currencySymbol})`}
                    value={formData.redeemValue}
                    onChange={(e) => handleInputChange('redeemValue', e.target.value)}
                    error={!!errors.redeemValue}
                    helperText={errors.redeemValue || 'Amount user gets when redeeming 1 unit'}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#5e72e4'
                        }
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e0e0e0', gap: 1.5 }}>
          <Button
            onClick={closeDialog}
            startIcon={<CancelIcon />}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#d0d7de',
              color: '#656d76',
              '&:hover': {
                borderColor: '#656d76',
                backgroundColor: '#f6f8fa'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveConfig}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.countryId}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: '#5e72e4',
              '&:hover': {
                backgroundColor: '#324cdd'
              },
              '&:disabled': {
                backgroundColor: '#e9ecef',
                color: '#6c757d'
              }
            }}
          >
            {editingConfig ? 'Update Configuration' : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};